import { phCapture } from "../lib/posthog";
import { PORTAL_SESSION_KEYS } from "../lib/purge-private-caches";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

/** Écran de connexion (route publique du portail). */
const LOGIN_URL = "/";

export class PortalApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "PortalApiError";
  }
}

function getToken(): string | null {
  return localStorage.getItem("portal_token");
}

/** Écran de changement de mot de passe forcé (cible des 403 mustChangePassword). */
const CHANGE_PASSWORD_URL = "/profile?changePassword=1";

/**
 * Tout ce qu'une session ouverte laisse dans le navigateur. Aligné sur
 * `logout()` de `usePortalAuth` — le portail ne doit pas connaître deux façons
 * de fermer une session.
 */
// D1-08 — LISTE UNIQUE, importée : `expireSession()` (401) et `logout()`
// (usePortalAuth) partagent la MÊME énumération. Deux listes recopiées avaient
// déjà divergé — la purge sur 401 laissait l'identité du client derrière elle
// sur un poste partagé.
const SESSION_KEYS = PORTAL_SESSION_KEYS;

/**
 * W7-C — CONTRAT 401, valable pour les quatre verbes : le portail OUBLIE la
 * session puis retourne à l'accueil.
 *
 * Avant, le 401 n'effaçait que le jeton et n'était gardé que par `apiGet` :
 *   — `portal_client` survivait, donc `isAuthenticated` restait vrai et
 *     `RequireAuth` continuait d'ouvrir les onze routes protégées avec un jeton
 *     mort, jusqu'au 401 suivant ;
 *   — un POST ou un PUT sur session expirée repartait en erreur générique
 *     (« Le service n'a pas répondu correctement »), session locale intacte.
 *
 * La redirection EST le contrat, et non un effet de bord : l'état
 * `unauthorized` de `PortalState` est la frame TRANSITOIRE affichée pendant que
 * le navigateur traite la navigation (l'affectation de `location.href` ne
 * suspend pas l'exécution : React rend une fois avant le déchargement). Elle
 * garantit qu'à cet instant l'écran dit « session expirée » plutôt qu'un faux
 * vide — aucune page ne dépend d'un rendu durable.
 */
function expireSession(): void {
  for (const key of SESSION_KEYS) localStorage.removeItem(key);
}

/**
 * Sommes-nous DÉJÀ sur l'écran de changement de mot de passe ? Sans ce garde-fou,
 * un 403 `mustChangePassword` déclenché par une requête émise DEPUIS cet écran
 * y renvoie à nouveau : le rechargement rejoue la requête, qui re-403 → boucle
 * de navigation infinie, le portail devient inutilisable au premier accès.
 */
function isOnChangePasswordScreen(): boolean {
  return (
    window.location.pathname === "/profile" &&
    new URLSearchParams(window.location.search).get("changePassword") === "1"
  );
}

/** Ce que l'API portail met dans le corps d'un refus, quand elle en met un. */
interface ApiErrorBody {
  mustChangePassword?: boolean;
  portalDisabled?: boolean;
  message?: string;
}

/**
 * Point de passage UNIQUE des réponses en échec : c'est ici, et nulle part
 * ailleurs, qu'un statut HTTP devient un sens pour le portail.
 */
async function raise(res: Response): Promise<never> {
  const text = await res.text().catch(() => "");
  let body: ApiErrorBody | null = null;
  try {
    body = JSON.parse(text) as ApiErrorBody;
  } catch {
    // Réponse en texte brut (502 d'un proxy, page HTML d'erreur…).
  }
  // Un corps JSON sans `message` ne doit pas être recraché tel quel à l'écran :
  // le client lirait `{"error":"E_TENANT"}`.
  const message = body?.message || (body ? res.statusText : text) || res.statusText;

  if (res.status === 401) {
    expireSession();
    // D1-08 — la redirection est OMISE si l'on est DÉJÀ sur l'écran de connexion :
    // un mot de passe erroné répond lui aussi 401, et recharger la page effacerait
    // le message d'erreur que le formulaire vient d'afficher.
    if (window.location.pathname !== LOGIN_URL) window.location.href = LOGIN_URL;
    // Message du serveur privilégié : sur /auth/login c'est le motif de refus,
    // pas une expiration de session.
    throw new PortalApiError(401, body?.message || "Session expirée");
  }

  if (res.status === 403) {
    if (body?.portalDisabled) {
      // L'artisan a coupé l'accès : la session locale ne vaut plus rien. Pas de
      // redirection ici — le client doit LIRE pourquoi son accès est fermé.
      expireSession();
      throw new PortalApiError(403, body.message || "Accès désactivé par votre fournisseur.");
    }
    if (body?.mustChangePassword) {
      if (!isOnChangePasswordScreen()) window.location.href = CHANGE_PASSWORD_URL;
      throw new PortalApiError(403, "Changement de mot de passe requis");
    }
  }

  throw new PortalApiError(res.status, message);
}

/** En-têtes toujours en clair : le reste du fichier les compose par étalement. */
type RequestOptions = Omit<RequestInit, "headers"> & { headers?: Record<string, string> };

async function request<T>(
  path: string,
  init: RequestOptions = {},
  opts: { asBlob?: boolean } = {},
): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) return raise(res);
  if (opts.asBlob) return (await res.blob()) as unknown as T;
  return (await res.json()) as T;
}

export function apiGet<T>(path: string, opts: { asBlob?: boolean } = {}): Promise<T> {
  return request<T>(path, {}, opts);
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * W7-C — envoi multipart (pièce jointe d'un message). Même base, même jeton,
 * mêmes gardes 401/403 que le reste du portail : l'envoi de message était le
 * SEUL appel authentifié à passer par un `fetch` nu, et une session expirée y
 * finissait en « Envoi impossible. Réessayez. » sur un compte déconnecté.
 *
 * Aucun `Content-Type` n'est posé : le navigateur écrit lui-même le boundary du
 * `FormData`, et le fixer à la main rendrait le corps illisible au serveur.
 */
export function apiPostForm<T>(path: string, form: FormData): Promise<T> {
  return request<T>(path, { method: "POST", body: form });
}

/**
 * LOT P4 — nature du document déduite du chemin API. On ne trace QUE le type
 * d'action (jamais l'identifiant ni le nom de fichier, qui portent des données
 * commerciales du client de l'artisan).
 */
export function documentKind(path: string): string {
  if (path.includes("/factures")) return "facture";
  if (path.includes("/avoirs")) return "avoir";
  if (path.includes("/bl")) return "bon_livraison";
  if (path.includes("/produits")) return "fiche_produit";
  if (path.includes("/prices")) return "tarifs";
  if (path.includes("/contracts")) return "contrat";
  return "document";
}

export async function downloadBlob(path: string, filename: string): Promise<void> {
  const blob = await apiGet<Blob>(path, { asBlob: true });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  // Point de passage UNIQUE des téléchargements PDF du portail (factures, avoirs,
  // BL, fiches produits, tarifs) — une seule instrumentation les couvre tous.
  phCapture("portal_cta_clic", { cta: `telechargement_${documentKind(path)}` });
}
